import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

const initialForm = {
  role_id: "",
  email: "",
  username: "",
  password: "",
  firstname: "",
  lastname: "",
  implementor_id: "",
  designation: "",
  supervisor_id: ""
};

const initialResetForm = {
  password: "",
  confirmPassword: ""
};

function UserFormPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const isEditing = Boolean(uuid);
  const [roles, setRoles] = useState([]);
  const [implementors, setImplementors] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [resetForm, setResetForm] = useState(initialResetForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchUser();
    }
  }, [uuid]);

  const submitLabel = useMemo(() => (isEditing ? "Update user" : "Create user"), [isEditing]);

  async function fetchLookups() {
    try {
      const [rolesResponse, implementorsResponse, usersResponse] = await Promise.all([
        api.get("/roles"),
        api.get("/implementors"),
        api.get("/users")
      ]);

      setRoles(rolesResponse.data || []);
      setImplementors(implementorsResponse.data || []);
      setUsers(usersResponse.data || []);
    } catch (err) {
      setError("Failed to load user lookups.");
    }
  }

  async function fetchUser() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/users/${uuid}`);
      const user = response.data;
      setForm({
        role_id: user.role_id || "",
        email: user.email || "",
        username: user.username || "",
        password: "",
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        implementor_id: user.implementor_id || "",
        designation: user.designation || "",
        supervisor_id: user.supervisor_id || ""
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load user.");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  }

  function handleResetChange(event) {
    const { name, value } = event.target;
    setResetForm((current) => ({
      ...current,
      [name]: value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);

    const payload = {
      role_id: Number(form.role_id),
      email: form.email,
      username: form.username,
      firstname: form.firstname,
      lastname: form.lastname,
      implementor_id: form.implementor_id ? Number(form.implementor_id) : null,
      designation: form.designation,
      supervisor_id: form.supervisor_id ? Number(form.supervisor_id) : null
    };

    if (!isEditing) {
      payload.password = form.password;
    }

    try {
      if (isEditing) {
        await api.put(`/users/${uuid}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }

      history.push("/users");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save user.");
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPassword(event) {
    event.preventDefault();

    if (resetForm.password.trim() === "") {
      toast.error("New password is required.");
      return;
    }

    if (resetForm.password !== resetForm.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setResetSaving(true);

    try {
      await api.put(`/users/${uuid}/reset-password`, {
        password: resetForm.password
      });
      setResetForm(initialResetForm);
      toast.success("Password reset");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reset password.");
    } finally {
      setResetSaving(false);
    }
  }

  return (
    <Row className="justify-content-center">
      <Col xs={12} xl={10}>
        <PageHeader
          title={isEditing ? "Edit User" : "Add User"}
          action={
            <Button variant="outline-secondary" onClick={() => history.push("/users")}>
              Back to users
            </Button>
          }
        />

        {loading ? <LoadingState label="Loading user..." /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}

        {!loading ? (
          <div className="content-card form-card">
            <form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">Role</label>
                  <Form.Select name="role_id" value={form.role_id} onChange={handleChange} required>
                    <option value="">Select role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <label className="form-label">Email</label>
                  <Form.Control type="email" name="email" value={form.email} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Username</label>
                  <Form.Control name="username" value={form.username} onChange={handleChange} required />
                </Col>
                {!isEditing ? (
                  <Col md={6}>
                    <label className="form-label">Password</label>
                    <Form.Control type="password" name="password" value={form.password} onChange={handleChange} required />
                  </Col>
                ) : null}
                <Col md={6}>
                  <label className="form-label">First name</label>
                  <Form.Control name="firstname" value={form.firstname} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Last name</label>
                  <Form.Control name="lastname" value={form.lastname} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Implementor</label>
                  <Form.Select name="implementor_id" value={form.implementor_id} onChange={handleChange}>
                    <option value="">No implementor</option>
                    {implementors.map((implementor) => (
                      <option key={implementor.id} value={implementor.id}>
                        {implementor.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <label className="form-label">Designation</label>
                  <Form.Control name="designation" value={form.designation} onChange={handleChange} />
                </Col>
                <Col md={6}>
                  <label className="form-label">Supervisor</label>
                  <Form.Select name="supervisor_id" value={form.supervisor_id} onChange={handleChange}>
                    <option value="">No supervisor</option>
                    {users
                      .filter((user) => user.uuid !== uuid)
                      .map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.firstname} {user.lastname}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : submitLabel}
                </Button>
                <Button variant="outline-secondary" onClick={() => history.push("/users")}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : null}

        {!loading && isEditing ? (
          <div className="content-card form-card mt-4">
            <h5 className="mb-3">Reset password</h5>
            <form onSubmit={handleResetPassword}>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">New password</label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={resetForm.password}
                    onChange={handleResetChange}
                    required
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Confirm new password</label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={resetForm.confirmPassword}
                    onChange={handleResetChange}
                    required
                  />
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" variant="warning" disabled={resetSaving}>
                  {resetSaving ? "Resetting..." : "Reset password"}
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export default UserFormPage;
